import { handleAxiosError, isIn } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { CustomRoleResult, PermissionLevel } from '@/client/custom-roles';
import { Actions, Subjects, SystemRoles } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { TranslationFunctionType } from '@/common/types';
import {
  AccessBlockedPanel,
  RoleCard,
  RolesAccessPageLayout,
  RolesAccessSection,
  RolesAccessSections,
} from '@/components/custom-roles';
import {
  useAccount,
  useAccountUsers,
  useCustomRoles,
  useDeleteCustomRole,
  useRoles,
} from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { useTrialRestriction } from '@/hooks/useTrialRestriction';
import { selectCurrentAccount } from '@/store/features/account';
import { DialogType } from '@/system-settings/enums';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import { scopeModeLabel, toPermissionSummaries } from '@/utils/custom-roles';
import { PlusIcon } from '@/utils/helpers/icons.helper';
import { PostHogFeatureFlags } from '@/utils/posthog';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { AccountHeader } from './AccountHeader';

const getPathItems = (
  account: Account,
  currentAccount: Account,
  t: TranslationFunctionType,
): MenuItem[] => [
  {
    label: currentAccount?.isSystem ? t('accounts') : t('account'),
    url: currentAccount?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNTS]()
      : RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('account.rolesAccess.tab'),
    url: currentAccount?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_ROLES_ACCESS](account?.id)
      : RedirectPaths[RedirectPathsEnum.ACCOUNT_ROLES_ACCESS](),
    template: AppBreadCrumbTemplate,
  },
];

export const AccountRolesAccessPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { setDialogData } = useContext(DialogContext);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { accountCanUseFeature } = useFeatureFlag();
  const { can } = usePermission();
  const canCreateRoles = can(Actions.CREATE, Subjects.ROLES);
  const canUpdateRoles = can(Actions.UPDATE, Subjects.ROLES);
  const canDeleteRoles = can(Actions.DELETE, Subjects.ROLES);

  const accountId = (
    currentAccount?.isSystem ? id : currentAccount?.id
  ) as string;
  const { account, isLoading } = useAccount({ accountId });
  const viewedAccount = currentAccount?.isSystem ? account : currentAccount;

  const isTrialLocked = useTrialRestriction(viewedAccount);

  const hasCustomRoles = accountCanUseFeature(
    PostHogFeatureFlags.CUSTOM_ROLES,
    viewedAccount?.featureFlagAbility ?? [],
  );

  const { customRoles, isLoading: areRolesLoading } = useCustomRoles(
    isTrialLocked || !hasCustomRoles ? '' : accountId,
  );
  const { remove } = useDeleteCustomRole();

  const { roles } = useRoles({ take: 50 });
  const builtInRoleIds = (code: SystemRoles) =>
    (roles?.result ?? [])
      .filter((role) => role.code === code)
      .map((role) => role.id);

  const adminRoleIds = builtInRoleIds(SystemRoles.ADMIN);
  const learnerRoleIds = builtInRoleIds(SystemRoles.LEARNER);

  const { users: admins } = useAccountUsers({
    accountId,
    take: 1,
    filters: [isIn('role', adminRoleIds)],
    enabled: !isTrialLocked && !!accountId && adminRoleIds.length > 0,
  });
  const { users: learners } = useAccountUsers({
    accountId,
    take: 1,
    filters: [isIn('role', learnerRoleIds)],
    enabled: !isTrialLocked && !!accountId && learnerRoleIds.length > 0,
  });

  const customRoleHolders = customRoles.reduce(
    (total, { assignedUserCount }) => total + assignedUserCount,
    0,
  );
  const learnerCount = Math.max((learners?.count ?? 0) - customRoleHolders, 0);

  const toCreate = () =>
    navigate(
      currentAccount?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_ROLES_ACCESS_CREATE](
            accountId,
          )
        : RedirectPaths[RedirectPathsEnum.ACCOUNT_ROLES_ACCESS_CREATE](),
    );

  const toEdit = (roleId: string) =>
    navigate(
      currentAccount?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNTS_ROLES_ACCESS_EDIT](
            accountId,
            roleId,
          )
        : RedirectPaths[RedirectPathsEnum.ACCOUNT_ROLES_ACCESS_EDIT](roleId),
    );

  const handleDelete = async ({
    customRole,
    assignedUserCount,
  }: CustomRoleResult) =>
    setDialogData({
      type: DialogType.CONFIRMATION,
      show: true,
      header: t('account.rolesAccess.delete.header', {
        name: customRole.name,
      }),
      message: t('account.rolesAccess.delete.message', {
        count: assignedUserCount,
        name: customRole.name,
      }),
      customContent: (
        <IrreversibleWarning>
          {t('account.rolesAccess.delete.irreversible')}
        </IrreversibleWarning>
      ),
      destructive: true,
      confirmButtonLabel: t('account.rolesAccess.delete.confirm'),
      onAccept: async () => {
        try {
          await remove({ accountId, id: customRole.id });
          toast?.success(
            t('toast.success'),
            t('account.rolesAccess.delete.success'),
          );
        } catch (e) {
          handleAxiosError(e, toast);
        }
      },
    });

  if (isLoading || !account || !currentAccount) {
    return (
      <FlexContainer direction="column" className="mt-5">
        <ProgressSpinner />
      </FlexContainer>
    );
  }

  return (
    <RolesAccessPageLayout>
      <AppBreadCrumb model={getPathItems(account, currentAccount, t)} />
      <AccountHeader
        selectedAccount={account}
        title={t('account.rolesAccess.tab')}
      />

      {!hasCustomRoles ? (
        <AccessBlockedPanel variant="no-access" />
      ) : isTrialLocked ? (
        <AccessBlockedPanel
          variant="free-trial"
          onBookMeeting={() =>
            navigate(RedirectPaths[RedirectPathsEnum.HUBSPOT_BOOKING]())
          }
        />
      ) : (
        <RolesAccessSections>
          <RolesAccessSection
            title={t('account.rolesAccess.builtIn.title')}
            subtitle={t('account.rolesAccess.builtIn.subtitle')}
          >
            <Grid>
              <RoleCard
                name={t('account.rolesAccess.builtIn.admin.name')}
                description={t('account.rolesAccess.builtIn.admin.description')}
                pills={[
                  {
                    key: 'manage',
                    label: t('account.rolesAccess.builtIn.admin.manageUsers'),
                    level: PermissionLevel.EDIT,
                  },
                  {
                    key: 'everything',
                    label: t('account.rolesAccess.builtIn.admin.everything'),
                    level: PermissionLevel.EDIT,
                  },
                ]}
                footer={t('account.rolesAccess.usersAssigned', {
                  count: admins?.count ?? 0,
                })}
              />
              <RoleCard
                name={t('account.rolesAccess.builtIn.learner.name')}
                description={t(
                  'account.rolesAccess.builtIn.learner.description',
                )}
                pills={[
                  {
                    key: 'none',
                    label: t('account.rolesAccess.builtIn.learner.noAccess'),
                    level: PermissionLevel.NONE,
                  },
                ]}
                footer={t('account.rolesAccess.usersAssigned', {
                  count: learnerCount,
                })}
              />
            </Grid>
          </RolesAccessSection>

          <RolesAccessSection
            title={t('account.rolesAccess.custom.title')}
            subtitle={t('account.rolesAccess.custom.subtitle')}
          >
            {areRolesLoading ? (
              <FlexContainer direction="column">
                <ProgressSpinner />
              </FlexContainer>
            ) : (
              <CustomRolesStack>
                {customRoles.length > 0 && (
                  <Grid>
                    {customRoles.map((result) => (
                      <RoleCard
                        key={result.customRole.id}
                        name={result.customRole.name}
                        pills={toPermissionSummaries(result.customRole, t)}
                        footer={[
                          scopeModeLabel(result.customRole.scopeMode, t),
                          t('account.rolesAccess.usersAssignedToRole', {
                            count: result.assignedUserCount,
                          }),
                        ]
                          .filter(Boolean)
                          .join('  |  ')}
                        editLabel={t('generic.edit')}
                        deleteLabel={t('delete')}
                        onEdit={
                          canUpdateRoles
                            ? () => toEdit(result.customRole.id)
                            : undefined
                        }
                        onDelete={
                          canDeleteRoles
                            ? () => handleDelete(result)
                            : undefined
                        }
                      />
                    ))}
                  </Grid>
                )}

                {canCreateRoles && (
                  <AddRoleRow type="button" onClick={toCreate}>
                    <PlusIcon />
                    <AddRoleLabel>
                      {t('account.rolesAccess.custom.add')}
                    </AddRoleLabel>
                  </AddRoleRow>
                )}
              </CustomRolesStack>
            )}
          </RolesAccessSection>
        </RolesAccessSections>
      )}
    </RolesAccessPageLayout>
  );
};

const IrreversibleWarning = styled.p`
  margin: var(--small-padding) 0 0;
  font-weight: var(--bold-font-weight);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr)); /* Figma: 2 per row */
  gap: var(--medium-padding); /* Figma 15px -> --medium-padding (16px) */

  @media screen and (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const CustomRolesStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding);
`;

const AddRoleRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--semi-xsmall-padding);
  width: 100%;
  padding: var(--small-padding) 0;
  background: none;
  border: 1px solid var(--gray-border-lighter);
  border-radius: var(--xsmall-border-radius);
  cursor: pointer;
`;

const AddRoleLabel = styled.span`
  font-size: var(--small-font-size);
  font-weight: var(--medium-font-weight);
  line-height: var(--small-line-height);
  color: var(--gray-darker);
`;
